import { Alert, Button, Link } from "../components";
import React, { useEffect, useRef, useState } from "react";
import { Recaptcha, ValidatedInput } from "./components";
import { getErrorMsg, validateForm } from "../services/validation-service";
import {
  getPasswordRecoveryState,
  initRecoverPassword,
  recoverPassword,
} from "../redux/auth-slice";
import { useDispatch, useSelector } from "react-redux";
import { StatusPage } from "./StatusPage";
import cn from "classnames";
import { passwordRecoveryValidation } from "../shared/validation";
import { scrollToFirstValidationError } from "../utils";
import sharedStyles from "../shared.module.scss";
import styles from "./PasswordRecoveryPage.module.scss";
import { useFormatMessage } from "../i18n";
import { useFormik } from "formik";
import { useLocation } from "react-router-dom";

const errorMessages = {
  NOT_FOUND: "this_email_does_not_exist",
  SERVER_ERROR: "something_went_wrong",
};

export function PasswordRecoveryPage() {
  const tr = useFormatMessage();
  const location = useLocation();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(initRecoverPassword());
  }, [dispatch]);

  const recaptchaRef = useRef();
  const [pristine, setPristine] = useState(true);
  const [recaptcha, setRecaptcha] = useState("");
  const [stage, setStage] = useState("password");

  const {
    recoverPasswordFailed,
    recoverPasswordError,
    recoverPasswordInProgress,
  } = useSelector(getPasswordRecoveryState);

  const errorMessage = tr(
    errorMessages[recoverPasswordError || "SERVER_ERROR"]
  );

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    onSubmit: handleSubmit,
  });

  const validationErrors = validateForm(
    { form: formik.values },
    passwordRecoveryValidation
  );

  function handleRecaptchaChange(value) {
    setRecaptcha(value);
  }

  function getPreviousPage() {
    return location.state?.fromAuthorize ? "/authorize" : "/login";
  }

  async function handleSubmit(values) {
    setPristine(false);
    if (validationErrors.length || !recaptcha) {
      scrollToFirstValidationError();
      return;
    }
    const { email } = values;
    const data = await dispatch(recoverPassword({ email, recaptcha }));
    if (data.meta.requestStatus === "fulfilled") {
      setStage("success");
    }
    recaptchaRef.current?.reset();
    setRecaptcha("");
    setPristine(true);
  }

  return (
    <>
      {stage === "success" && (
        <StatusPage
          text={tr(
            "you_will_receive_an_email_soon_please_follow_the_instructions_for_changing_the_password"
          )}
          variant="success"
          buttonTitle={"go_back"}
          nextPage={getPreviousPage()}
        />
      )}
      {stage === "password" && (
        <div className={sharedStyles.content}>
          <form onSubmit={formik.handleSubmit} className={sharedStyles.form}>
            <h4 className={cn(sharedStyles.title, sharedStyles.pageTitle)}>
              {tr("i_have_lost_my_password")}
            </h4>
            <h5 className={cn(sharedStyles.subtitle, sharedStyles.mainText)}>
              {tr("give_us_your_email_in_order_to_get_a_new_password")}
            </h5>
            <ValidatedInput
              label={tr("email")}
              id="email-input"
              name="email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              errors={getErrorMsg(validationErrors, "email")}
              pristine={pristine}
            />
            <Recaptcha
              rref={recaptchaRef}
              onChange={handleRecaptchaChange}
              pristine={pristine}
              recaptcha={recaptcha}
            />
            {recoverPasswordFailed && (
              <Alert className={styles.error} severity="error">
                {errorMessage}
              </Alert>
            )}
            <Button
              type="submit"
              title={tr("recover_password")}
              disabled={recoverPasswordInProgress}
              variant="primary"
              loading={recoverPasswordInProgress}
            />
            <span className={styles.backButton}>
              {`${tr("already_have_an_account")} `}
              <Link to={getPreviousPage()}>
                {tr("already_have_an_account_login")}
              </Link>
            </span>
          </form>
        </div>
      )}
    </>
  );
}
